import React, { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useMutation } from '@tanstack/react-query';
import Router from 'next/router';

import { useAccountsService, useWorkersService } from '@restworld/data-services';
import { Worker } from '@restworld/utility-types';
import { useAuth } from '@restworld/feature-authentication';
import { scrollToView, useToastMessageContext } from '@restworld/utils-common';

import { renderComponentObjectMapping } from '../profile-edit';
import { getWorkerAtom, setWorkerAtom } from '../../global-state.worker-data';
import { referencesConsentModalVisibleAtom } from '../../worker-references/references-consent-modal';

type SubmitHandlerProps = {
  onClose?: () => void;
  type?: 'personalInfo' | 'other';
  editPageType?: keyof typeof renderComponentObjectMapping;
  editIndex?: number;
  mode?: 'edit' | 'add';
};

const useUpdateWorkerMutation = ({ type, editPageType, editIndex, onClose, mode }: SubmitHandlerProps) => {
  const workerService = useWorkersService();
  const accountService = useAccountsService();
  const { t } = useTranslation();

  const setReferencesConsentModalVisible = useSetAtom(referencesConsentModalVisibleAtom);
  const onSetWorkerData = useSetAtom(setWorkerAtom);
  const { initializeAuthentication } = useAuth();
  const { setToastMessage, toastMessageOptions } = useToastMessageContext();

  const openReferencesConsentModal = useCallback(() => {
    setTimeout(() => {
      Router.push(`/profilo#added_past_experiences`, undefined, { shallow: true });
      setReferencesConsentModalVisible(true);
    }, 1500);
  }, [setReferencesConsentModalVisible]);

  const onSuccessCallback = useCallback(
    async (data: Worker) => {
      const scrollIndex = `${editPageType}${editIndex > -1 ? `-${editIndex}` : ''}`;

      if (type === 'personalInfo') {
        const userResponse = await accountService.me();
        onSetWorkerData({
          ...data,
          user: {
            ...data?.user,
            phone_number: userResponse?.data?.data?.phone_number,
            email: userResponse?.data?.data?.email,
          },
        });
        initializeAuthentication();
      } else onSetWorkerData({ ...data });

      onClose?.();
      setToastMessage({
        ...toastMessageOptions,
        message: t('onboarding-page:info_updated_correctly'),
        duration: 5000,
        visible: true,
        type: 'success',
        position: 'bottom',
        ui: 'alert',
      });

      if (mode === 'add' && editPageType === 'work_experience') openReferencesConsentModal();

      setTimeout(() => {
        scrollToView(scrollIndex);
      }, 400);
    },
    [
      accountService,
      editIndex,
      editPageType,
      initializeAuthentication,
      mode,
      onClose,
      onSetWorkerData,
      openReferencesConsentModal,
      setToastMessage,
      t,
      toastMessageOptions,
      type,
    ]
  );

  const updateWorker = React.useCallback(
    async (params: { workerId: string; params: Partial<Worker> }) => {
      return (await workerService.updateWorker(params)).data;
    },
    [workerService]
  );
  return useMutation({
    mutationFn: updateWorker,
    onSuccess(data) {
      onSuccessCallback(data);
    },
    onError(error, variables) {
      console.error(error, `error updating worker with params: ${variables}`);
    },
  });
};

export const useApiSubmitHandler = (props?: SubmitHandlerProps) => {
  const { data: workerData } = useAtomValue(getWorkerAtom);
  const { mutate: updateWorker, isPending } = useUpdateWorkerMutation({ ...props });

  const apiSubmitHandler = useCallback(
    async (params: Partial<Worker>) => {
      updateWorker({
        workerId: workerData?.user_id ?? '',
        params: {
          ...params,
        },
      });
    },
    [updateWorker, workerData?.user_id]
  );

  return {
    apiSubmitHandler,
    loading: isPending,
  };
};
